import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tabs,
  Typography,
  Spin,
  Alert,
  Tag,
  Space,
  Button,
  Modal,
  Descriptions,
  Row,
  Col,
  Divider,
  Input,
  Form,
  Select,
  DatePicker,
  InputNumber,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  InsuranceOutlined,
  FileTextOutlined,
  BankOutlined,
  EyeOutlined,
  SearchOutlined,
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  GiftOutlined,
  QrcodeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  GET_INSURANCE_POLICIES_BY_PAYOR,
  GET_INSURANCE_POLICIES_BY_PAYOR_PAGINATED,
  GET_INSURANCE_PLANS_BY_PAYOR,
  GET_APP_USERS,
  GET_INSURANCE_COUPONS_BY_PAYOR_PAGINATED,
  GET_UNUSED_INSURANCE_COUPONS_BY_PAYOR,
  GET_IS_COUPON_PAYOR,
  GET_CLAIMS_EXPORT_BY_DATE_RANGE,
} from '../graphql/queries';
import {
  CREATE_INSURANCE_POLICY,
  UPDATE_INSURANCE_POLICY,
  BATCH_CREATE_INSURANCE_COUPONS,
} from '../graphql/mutations';
import { useTranslation } from '../hooks/useTranslation';
import { usePolicyBalanceDialog } from '../contexts/Dialogs/PolicyBalanceDialogContext';
import { usePlanDetailDialog } from '../contexts/Dialogs/PlanDetailDialogContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface CoverageItem {
  name: string;
  description?: string;
  type: string;
  coverage_amount?: number;
  copayment_amount?: number;
  coverage_times_per_month?: number;
}

interface InsurancePlan {
  id: string;
  name: string;
  payor: string;
  description: string;
  planDetails?: {
    free_consultations?: number;
    free_consultations_per_year?: number;
    free_consultations_per_month?: number;
    free_consultations_per_week?: number;
    coverage?: CoverageItem[];
  };
  createdAt: string;
  updatedAt: string;
}

interface InsuranceCoupon {
  id: string;
  payor: string;
  couponCode: string;
  startDate?: string;
  endDate?: string;
  appUserId?: string;
  appointmentId?: string;
  policyDetails: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  plan?: InsurancePlan;
  appUser?: {
    id: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    phone_number?: string;
  };
  appointment?: {
    id: string;
    date: string;
    time: string;
    status: string;
    type: string;
  };
}

interface ClaimsExportRow {
  claimId?: string;
  policyNumber?: string;
  policyName?: string;
  planName?: string;
  appointmentId?: string;
  appointmentDate?: string;
  approvedAmount?: string;
  patientName?: string;
  doctorName?: string;
  clinicName?: string;
}

const InsurancePayorDetail: React.FC = () => {
  const { payor } = useParams<{ payor: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openDialog: openPolicyBalanceDialog } = usePolicyBalanceDialog();
  const { openDialog: openPlanDetailDialog } = usePlanDetailDialog();
  
  // Query to check if payor is a coupon type
  const {
    data: isCouponPayorData,
    loading: isCouponPayorLoading,
  } = useQuery(GET_IS_COUPON_PAYOR, {
    variables: { payor: payor as any },
    skip: !payor,
  });
  
  const isCouponType = isCouponPayorData?.isCouponPayor || false;
  const [activeTab, setActiveTab] = useState('policies');
  
  // Update active tab when isCouponType is determined
  useEffect(() => {
    if (!isCouponPayorLoading && isCouponType) {
      setActiveTab('coupons');
    }
  }, [isCouponPayorLoading, isCouponType]);
  
  const [policySearchTerm, setPolicySearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isAddMemberModalVisible, setIsAddMemberModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedPolicyForEdit, setSelectedPolicyForEdit] = useState<any>(null);
  const [isBatchCouponModalVisible, setIsBatchCouponModalVisible] = useState(false);
  const [selectedPlanForCoupon, setSelectedPlanForCoupon] = useState<InsurancePlan | null>(null);
  const [isClaimsExportModalVisible, setIsClaimsExportModalVisible] = useState(false);
  const [selectedClaimsExportDateRange, setSelectedClaimsExportDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [addMemberForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [batchCouponForm] = Form.useForm();
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [couponPagination, setCouponPagination] = useState({ page: 1, pageSize: 10 });

  const { data: appUsersData, loading: appUsersLoading } = useQuery(GET_APP_USERS);
  
  const [createInsurancePolicy, { loading: createLoading }] = useMutation(CREATE_INSURANCE_POLICY, {
    refetchQueries: [{ 
      query: GET_INSURANCE_POLICIES_BY_PAYOR_PAGINATED, 
      variables: { 
        payor, 
        paginationDto: { 
          page: pagination.page, 
          limit: pagination.pageSize,
          search: debouncedSearchTerm || undefined,
        } 
      } 
    }],
    onCompleted: () => {
      message.success('Insurance policy created successfully');
      setIsAddMemberModalVisible(false);
      addMemberForm.resetFields();
    },
    onError: (error) => {
      message.error(`Failed to create insurance policy: ${error.message}`);
    },
  });

  const [updateInsurancePolicy, { loading: updateLoading }] = useMutation(UPDATE_INSURANCE_POLICY, {
    refetchQueries: [{ 
      query: GET_INSURANCE_POLICIES_BY_PAYOR_PAGINATED, 
      variables: { 
        payor, 
        paginationDto: { 
          page: pagination.page, 
          limit: pagination.pageSize,
          search: debouncedSearchTerm || undefined,
        } 
      } 
    }],
    onCompleted: () => {
      message.success('Insurance policy updated successfully');
      setIsEditModalVisible(false);
      setSelectedPolicyForEdit(null);
      editForm.resetFields();
    },
    onError: (error) => {
      message.error(`Failed to update insurance policy: ${error.message}`);
    },
  });

  const {
    data: policiesData,
    loading: policiesLoading,
    error: policiesError,
    refetch: refetchPolicies,
  } = useQuery(GET_INSURANCE_POLICIES_BY_PAYOR_PAGINATED, {
    variables: { 
      payor, 
      paginationDto: { 
        page: pagination.page, 
        limit: pagination.pageSize,
        search: debouncedSearchTerm || undefined,
      } 
    },
    skip: !payor,
  });

  const {
    data: plansData,
    loading: plansLoading,
    error: plansError,
  } = useQuery(GET_INSURANCE_PLANS_BY_PAYOR, {
    variables: { payor },
  });

  const couponPaginationDto = {
    page: couponPagination.page,
    limit: couponPagination.pageSize,
  };

  const {
    data: couponsData,
    loading: couponsLoading,
    error: couponsError,
  } = useQuery(GET_INSURANCE_COUPONS_BY_PAYOR_PAGINATED, {
    variables: { payor, paginationDto: couponPaginationDto },
    skip: !payor || !isCouponType,
  });

  const [batchCreateCoupons, { loading: batchCreateLoading }] = useMutation(BATCH_CREATE_INSURANCE_COUPONS, {
    refetchQueries: [{ 
      query: GET_INSURANCE_COUPONS_BY_PAYOR_PAGINATED, 
      variables: { payor, paginationDto: couponPaginationDto } 
    }],
    onCompleted: () => {
      message.success('Coupons generated successfully');
      setIsBatchCouponModalVisible(false);
      setSelectedPlanForCoupon(null);
      batchCouponForm.resetFields();
    },
    onError: (error) => {
      message.error(`Failed to generate coupons: ${error.message}`);
    },
  });
  const [getClaimsExportByDateRange, { loading: claimsExportLoading }] = useLazyQuery(GET_CLAIMS_EXPORT_BY_DATE_RANGE);
  const [getUnusedCouponsForExport, { loading: couponsExportLoading }] = useLazyQuery(GET_UNUSED_INSURANCE_COUPONS_BY_PAYOR, {
    fetchPolicy: 'network-only',
  });

  const formatPayorName = (payorName: string) => {
    return payorName
      .split('')
      .map((char, index) => {
        if (index === 0) return char.toUpperCase();
        if (char === char.toUpperCase() || payorName[index - 1] === ' ') {
          return char;
        }
        return char;
      })
      .join('')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase());
  };

  const policies = policiesData?.insurancePoliciesByPayorPaginated?.policies || [];
  const paginationInfo = policiesData?.insurancePoliciesByPayorPaginated?.pagination;
  const plans = plansData?.insurancePlansByPayor || [];
  const coupons = couponsData?.insuranceCouponsByPayorPaginated?.coupons || [];
  const couponPaginationInfo = couponsData?.insuranceCouponsByPayorPaginated?.pagination;
  const appUsers = appUsersData?.users || [];

  const handleBatchGenerateCoupons = async (values: any) => {
    if (!selectedPlanForCoupon) return;
    
    try {
      await batchCreateCoupons({
        variables: {
          batchCreateInsuranceCouponsDto: {
            planId: selectedPlanForCoupon.id,
            count: values.count,
            prefix: values.prefix ? values.prefix.trim().toUpperCase() : undefined,
            startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
            endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
          },
        },
      });
    } catch (error) {
      console.error('Error generating coupons:', error);
    }
  };

  const handleOpenBatchCouponModal = (plan: InsurancePlan) => {
    setSelectedPlanForCoupon(plan);
    setIsBatchCouponModalVisible(true);
  };

  // Handle pagination change
  const handleTableChange = (newPagination: any) => {
    setPagination({
      page: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    });
  };

  // Debounce search - only trigger search after 500ms of no typing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Update input state immediately for UI responsiveness
    setPolicySearchTerm(value);
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce timer - search only triggers after 500ms of no typing
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 500);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset to page 1 when debounced search term changes
  useEffect(() => {
    if (!payor) return;
    setPagination((prev) => ({ page: 1, pageSize: prev.pageSize }));
    // Note: useQuery will automatically refetch when debouncedSearchTerm changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, payor]);

  const handleAddMember = async (values: any) => {
    try {
      // Normalize payor to lowercase to match enum
      const normalizedPayor = payor?.toLowerCase() || 'townhealth';
      
      // Build the DTO, only including fields that have values
      const createDto: any = {
        name: values.name,
        payor: normalizedPayor,
        planId: values.planId,
        description: values.description || values.name, // Use name as fallback if description is empty
        policyDetails: plans.find((p: InsurancePlan) => p.id === values.planId)?.planDetails || {},
        isActive: true,
      };

      // Only add optional fields if they have values
      if (values.policyNumber && values.policyNumber.trim()) {
        createDto.policyNumber = values.policyNumber;
      }
      if (values.startDate && values.startDate.isValid && values.startDate.isValid()) {
        const dateStr = values.startDate.format('YYYY-MM-DD');
        if (dateStr && dateStr !== 'Invalid date') {
          createDto.startDate = dateStr;
        }
      }
      if (values.endDate && values.endDate.isValid && values.endDate.isValid()) {
        const dateStr = values.endDate.format('YYYY-MM-DD');
        if (dateStr && dateStr !== 'Invalid date') {
          createDto.endDate = dateStr;
        }
      }
      if (values.appUserId) {
        createDto.appUserId = values.appUserId;
      }

      await createInsurancePolicy({
        variables: {
          createInsurancePolicyDto: createDto,
        },
      });
    } catch (error) {
      console.error('Error creating insurance policy:', error);
    }
  };

  const handleEditPolicy = (record: any) => {
    setSelectedPolicyForEdit(record);
    editForm.setFieldsValue({
      startDate: record.startDate ? dayjs(record.startDate) : null,
      endDate: record.endDate ? dayjs(record.endDate) : null,
    });
    setIsEditModalVisible(true);
  };

  const handleUpdatePolicy = async (values: any) => {
    if (!selectedPolicyForEdit) return;
    
    try {
      const updateDto: any = {};
      
      // Only include date fields if they have valid values
      if (values.startDate && values.startDate.isValid()) {
        updateDto.startDate = values.startDate.format('YYYY-MM-DD');
      } else if (values.startDate === null || values.startDate === undefined) {
        // Allow clearing the date by sending null
        updateDto.startDate = null;
      }
      if (values.endDate && values.endDate.isValid()) {
        updateDto.endDate = values.endDate.format('YYYY-MM-DD');
      } else if (values.endDate === null || values.endDate === undefined) {
        // Allow clearing the date by sending null
        updateDto.endDate = null;
      }
      
      await updateInsurancePolicy({
        variables: {
          id: selectedPolicyForEdit.id,
          updateInsurancePolicyDto: updateDto,
        },
      });
    } catch (error) {
      console.error('Error updating insurance policy:', error);
    }
  };

  // Note: Filtering is now handled by the backend via paginationDto.search
  const filteredPolicies = policies;

  const policiesColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Policy Number',
      dataIndex: 'policyNumber',
      key: 'policyNumber',
      render: (text: string) => text || '-',
    },
    {
      title: 'Plan',
      dataIndex: ['plan'],
      key: 'plan',
      render: (plan: any) => {
        if (!plan) return '-';
        return (
          <Button
            type="link"
            onClick={() => {
              openPlanDetailDialog({
                id: plan.id,
                name: plan.name,
                payor: plan.payor,
                description: plan.description,
                planDetails: plan.planDetails,
                createdAt: plan.createdAt || '',
                updatedAt: plan.updatedAt || '',
              });
            }}
            style={{ padding: 0 }}
          >
            {plan.name}
          </Button>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'User',
      dataIndex: ['appUser', 'firstname'],
      key: 'user',
      render: (_: any, record: any) => {
        if (record.appUser) {
          return `${record.appUser.firstname} ${record.appUser.lastname}`;
        }
        return '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        return (
          <Space>
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditPolicy(record)}
            >
              Edit
            </Button>
            {record.appUserId && (
              <Button
                type="primary"
                icon={<DollarOutlined />}
                size="small"
                onClick={() => {
                  openPolicyBalanceDialog(record.id, record.name, record.policyNumber);
                }}
              >
                View Balance
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const handlePlanClick = (plan: InsurancePlan) => {
    openPlanDetailDialog(plan);
  };

  const showClaimsExportModal = () => {
    const today = dayjs();
    setSelectedClaimsExportDateRange([today, today]);
    setIsClaimsExportModalVisible(true);
  };

  const exportClaimsByDateRange = async (dateRange: [dayjs.Dayjs, dayjs.Dayjs]) => {
    if (!payor) {
      message.error('Payor is missing');
      return;
    }

    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const graphqlPayor = payor.toUpperCase().replace(/-/g, '_');
      const { data } = await getClaimsExportByDateRange({
        variables: {
          payor: graphqlPayor,
          startDate,
          endDate,
        },
      });

      const claims = data?.getClaimsExportByDateRange || [];
      if (!claims.length) {
        message.warning('No claims found for the selected date range');
        return;
      }

      const headers = [
        'Claim ID',
        'Policy Number',
        'Policy Name',
        'Plan Name',
        'Appointment ID',
        'Appointment Date',
        'Approved Amount',
        'Patient Name',
        'Doctor Name',
        'Clinic Name',
      ];

      const rows = claims.map((claim: ClaimsExportRow) => {
        return [
          `"${claim.claimId || ''}"`,
          `"${claim.policyNumber || ''}"`,
          `"${claim.policyName || ''}"`,
          `"${claim.planName || ''}"`,
          `"${claim.appointmentId || ''}"`,
          `"${claim.appointmentDate || ''}"`,
          `"${claim.approvedAmount || ''}"`,
          `"${(claim.patientName || '').replace(/"/g, '""')}"`,
          `"${(claim.doctorName || '').replace(/"/g, '""')}"`,
          `"${(claim.clinicName || '').replace(/"/g, '""')}"`,
        ].join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `claims-export-${payor.toLowerCase()}-${startDate === endDate ? startDate : `${startDate}_to_${endDate}`}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`Claims exported successfully! ${claims.length} claims included.`);
    } catch (error) {
      message.error(`Failed to export claims: ${(error as Error).message}`);
    }
  };

  const handleClaimsExportConfirm = async () => {
    if (!selectedClaimsExportDateRange) {
      message.warning('Please select a date range');
      return;
    }

    setIsClaimsExportModalVisible(false);
    await exportClaimsByDateRange(selectedClaimsExportDateRange);
  };

  const handleExportCoupons = async () => {
    try {
      // Fetch the full set of unused coupons on demand (the table itself is paginated).
      const { data } = await getUnusedCouponsForExport({ variables: { payor: payor as any } });
      const exportableCoupons: InsuranceCoupon[] = data?.unusedInsuranceCouponsByPayor || [];

      if (!exportableCoupons.length) {
        message.warning('No unused coupons available to export');
        return;
      }

      const escape = (value: string | undefined | null) =>
        `"${String(value ?? '').replace(/"/g, '""')}"`;

      const headers = [
        'Coupon Code',
        'Plan',
        'Start Date',
        'End Date',
        'Status',
        'Created At',
      ];

      const rows = exportableCoupons.map((coupon) => {
        return [
          escape(coupon.couponCode),
          escape(coupon.plan?.name),
          escape(coupon.startDate ? dayjs(coupon.startDate).format('YYYY-MM-DD') : ''),
          escape(coupon.endDate ? dayjs(coupon.endDate).format('YYYY-MM-DD') : ''),
          escape('Active'),
          escape(coupon.createdAt ? dayjs(coupon.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''),
        ].join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `coupons-${(payor || 'export').toLowerCase()}-${dayjs().format('YYYY-MM-DD')}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`Exported ${exportableCoupons.length} unused coupon(s).`);
    } catch (error) {
      message.error(`Failed to export coupons: ${(error as Error).message}`);
    }
  };


  const plansColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: InsurancePlan) => (
        <Button
          type="link"
          onClick={() => handlePlanClick(record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      render: (_: any, record: InsurancePlan) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handlePlanClick(record)}
          >
            View Details
          </Button>
          {isCouponType && (
            <Button
              type="primary"
              icon={<QrcodeOutlined />}
              onClick={() => handleOpenBatchCouponModal(record)}
            >
              Generate Coupons
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const couponsColumns = [
    {
      title: 'Coupon Code',
      dataIndex: 'couponCode',
      key: 'couponCode',
      render: (text: string) => (
        <Text strong style={{ fontFamily: 'monospace' }}>
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Plan',
      dataIndex: ['plan'],
      key: 'plan',
      render: (plan: any) => {
        if (!plan) return '-';
        return (
          <Button
            type="link"
            onClick={() => {
              openPlanDetailDialog({
                id: plan.id,
                name: plan.name,
                payor: plan.payor,
                description: plan.description,
                planDetails: plan.planDetails,
                createdAt: plan.createdAt || '',
                updatedAt: plan.updatedAt || '',
              });
            }}
            style={{ padding: 0 }}
          >
            {plan.name}
          </Button>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'Appointment',
      dataIndex: ['appointment', 'id'],
      key: 'appointment',
      render: (_: any, record: InsuranceCoupon) => {
        if (record.appointment) {
          const appointmentDate = record.appointment.date ? dayjs(record.appointment.date).format('YYYY-MM-DD') : '';
          const appointmentTime = record.appointment.time || '';
          const appointmentId = record.appointment.id;
          return (
            <div>
              <div>
                <Button
                  type="link"
                  onClick={() => appointmentId && navigate(`/appointments/${appointmentId}`)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  <Text strong>{appointmentId}</Text>
                </Button>
              </div>
              {appointmentDate && (
                <div><Text type="secondary">{appointmentDate} {appointmentTime}</Text></div>
              )}
              {record.appointment.status && (
                <div><Tag>{record.appointment.status}</Tag></div>
              )}
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/insurance')}
        >
          Back
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={showClaimsExportModal}
          loading={claimsExportLoading}
        >
          Export Claims
        </Button>
      </Space>

      <Title level={2} style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        {payor === 'townhealth' || payor === 'TOWNHEALTH' ? (
          <img
            src="/images/Townhealth_Logo.png"
            alt="Town Health logo"
            style={{
              maxHeight: '60px',
              objectFit: 'contain',
            }}
          />
        ) : (
          <InsuranceOutlined />
        )}
        {payor ? formatPayorName(payor) : 'Insurance Payor'}
      </Title>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {!isCouponType && (
            <TabPane
              tab={
                <span>
                  <FileTextOutlined />
                  Policies ({paginationInfo?.total || 0})
                </span>
              }
              key="policies"
            >
            {policiesLoading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : policiesError ? (
              <Alert
                message={t('common.error')}
                description={policiesError.message}
                type="error"
                showIcon
              />
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Search
                    placeholder="Search by policy number or name"
                    value={policySearchTerm}
                    onChange={handleSearchChange}
                    allowClear
                    prefix={<SearchOutlined />}
                    style={{ maxWidth: 400 }}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsAddMemberModalVisible(true)}
                  >
                    Add Member
                  </Button>
                </div>
                <Table
                  dataSource={filteredPolicies}
                  columns={policiesColumns}
                  rowKey="id"
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    total: paginationInfo?.total || 0,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} policies`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                  }}
                  onChange={handleTableChange}
                  loading={policiesLoading}
                />
              </>
            )}
            </TabPane>
          )}
          {isCouponType && (
            <TabPane
              tab={
                <span>
                  <GiftOutlined />
                  Coupons ({couponPaginationInfo?.total || 0})
                </span>
              }
              key="coupons"
            >
              {couponsLoading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                </div>
              ) : couponsError ? (
                <Alert
                  message={t('common.error')}
                  description={couponsError.message}
                  type="error"
                  showIcon
                />
              ) : (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExportCoupons}
                      loading={couponsExportLoading}
                    >
                      Export Coupons (CSV)
                    </Button>
                  </div>
                  <Table
                    dataSource={coupons}
                    columns={couponsColumns}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                      current: couponPagination.page,
                      pageSize: couponPagination.pageSize,
                      total: couponPaginationInfo?.total || 0,
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} coupons`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    onChange={(newPagination) =>
                      setCouponPagination({
                        page: newPagination.current || 1,
                        pageSize: newPagination.pageSize || 10,
                      })
                    }
                    loading={couponsLoading}
                  />
                </>
              )}
            </TabPane>
          )}
          <TabPane
            tab={
              <span>
                <BankOutlined />
                Plans ({plans.length})
              </span>
            }
            key="plans"
          >
            {plansLoading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : plansError ? (
              <Alert
                message={t('common.error')}
                description={plansError.message}
                type="error"
                showIcon
              />
            ) : (
              <Table
                dataSource={plans}
                columns={plansColumns}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 10 }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Add Member Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlusOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Add New Member
          </div>
        }
        open={isAddMemberModalVisible}
        onCancel={() => {
          setIsAddMemberModalVisible(false);
          addMemberForm.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={addMemberForm}
          layout="vertical"
          onFinish={handleAddMember}
        >
          <Form.Item
            name="name"
            label="Policy Name"
            rules={[{ required: true, message: 'Please enter policy name' }]}
          >
            <Input placeholder="Enter policy name" />
          </Form.Item>

          <Form.Item
            name="appUserId"
            label="User"
          >
            <Select
              placeholder="Select user (optional)"
              allowClear
              loading={appUsersLoading}
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {appUsers.map((user: any) => (
                <Option key={user.id} value={user.id}>
                  {user.firstname} {user.lastname} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="planId"
            label="Plan"
            rules={[{ required: true, message: 'Please select a plan' }]}
          >
            <Select placeholder="Select plan">
              {plans.map((plan: InsurancePlan) => (
                <Option key={plan.id} value={plan.id}>
                  {plan.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="policyNumber"
            label="Policy Number"
          >
            <Input placeholder="Enter policy number (optional)" />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
              >
                Create
              </Button>
              <Button
                onClick={() => {
                  setIsAddMemberModalVisible(false);
                  addMemberForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Policy Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EditOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Edit Policy Dates
          </div>
        }
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedPolicyForEdit(null);
          editForm.resetFields();
        }}
        footer={null}
        width={500}
        destroyOnClose
      >
        {selectedPolicyForEdit && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Policy: </Text>
            <Text>{selectedPolicyForEdit.name}</Text>
            {selectedPolicyForEdit.policyNumber && (
              <>
                <br />
                <Text strong>Policy Number: </Text>
                <Text>{selectedPolicyForEdit.policyNumber}</Text>
              </>
            )}
          </div>
        )}
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdatePolicy}
        >
          <Form.Item
            name="startDate"
            label="Start Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End Date"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateLoading}
              >
                Update
              </Button>
              <Button
                onClick={() => {
                  setIsEditModalVisible(false);
                  setSelectedPolicyForEdit(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Batch Generate Coupons Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <QrcodeOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Generate Batch Coupons
          </div>
        }
        open={isBatchCouponModalVisible}
        onCancel={() => {
          setIsBatchCouponModalVisible(false);
          setSelectedPlanForCoupon(null);
          batchCouponForm.resetFields();
        }}
        footer={null}
        width={500}
        destroyOnClose
      >
        {selectedPlanForCoupon && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Plan: </Text>
            <Text>{selectedPlanForCoupon.name}</Text>
          </div>
        )}
        <Form
          form={batchCouponForm}
          layout="vertical"
          onFinish={handleBatchGenerateCoupons}
        >
          <Form.Item
            name="count"
            label="Number of Coupons"
            rules={[
              { required: true, message: 'Please enter the number of coupons' },
              { type: 'number', min: 1, message: 'Must be at least 1' },
              { type: 'number', max: 1000, message: 'Cannot exceed 1000' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter number of coupons to generate"
              min={1}
              max={1000}
            />
          </Form.Item>

          <Form.Item
            name="prefix"
            label="Prefix (Optional)"
            help="Leave empty to use payor name as prefix. Only uppercase letters and numbers allowed."
            normalize={(value) => {
              // Auto-uppercase and filter non-alphanumeric
              if (!value) return value;
              return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            }}
            rules={[
              { max: 50, message: 'Prefix must not exceed 50 characters' },
              { pattern: /^[A-Z0-9]*$/, message: 'Prefix must contain only uppercase letters and numbers' },
            ]}
          >
            <Input
              placeholder={`Default: ${selectedPlanForCoupon?.payor?.toUpperCase() || 'PAYOR'}`}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start Date (Optional)"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End Date (Optional)"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={batchCreateLoading}
              >
                Generate
              </Button>
              <Button
                onClick={() => {
                  setIsBatchCouponModalVisible(false);
                  setSelectedPlanForCoupon(null);
                  batchCouponForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Select Claims Export Date Range"
        open={isClaimsExportModalVisible}
        onOk={handleClaimsExportConfirm}
        onCancel={() => setIsClaimsExportModalVisible(false)}
        okText="Export"
        cancelText="Cancel"
        confirmLoading={claimsExportLoading}
      >
        <div style={{ padding: '20px 0' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Please select a date range for claim export:
          </Text>
          <RangePicker
            value={selectedClaimsExportDateRange}
            onChange={(dates) => setSelectedClaimsExportDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size="small"
              onClick={() => {
                const today = dayjs();
                setSelectedClaimsExportDateRange([today, today]);
              }}
            >
              Today
            </Button>
            <Button
              size="small"
              onClick={() => {
                const today = dayjs();
                const last7Days = today.subtract(6, 'day');
                setSelectedClaimsExportDateRange([last7Days, today]);
              }}
            >
              Last 7 Days
            </Button>
            <Button
              size="small"
              onClick={() => {
                const today = dayjs();
                const thisMonthStart = today.startOf('month');
                setSelectedClaimsExportDateRange([thisMonthStart, today]);
              }}
            >
              This Month
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InsurancePayorDetail;

